import { useCallback, useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, Marker, CircleMarker, Circle, Tooltip, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Search } from "lucide-react";
import { api } from "@/lib/api";
import { useSync } from "@/lib/live";
import { useDetail } from "@/admin/AdminDetail";

const JAIPUR = [26.9124, 75.7873];
export const LAYERS = [
  ["customers", "Customers", "#3B6FD8"], ["pending", "Pending pickups", "#E05C3A"], ["active", "Active pickups", "#E0A43A"],
  ["online", "Kabadiwalas online", "#16A34A"], ["offline", "Kabadiwalas offline", "#9CA3AF"], ["radius", "Service radius", "#2E9E6B"],
];
const COLOR = Object.fromEntries(LAYERS.map(([k, , c]) => [k, c]));

const truck = (p) => L.divIcon({
  className: "",
  html: `<div style="display:flex;align-items:center;gap:4px;transform:translate(-13px,-13px)"><div style="width:26px;height:26px;border-radius:9px;background:${p.live ? "#16A34A" : "#9CA3AF"};border:3px solid #fff;box-shadow:0 3px 8px rgba(0,0,0,.35);display:grid;place-items:center;color:#fff;font:800 11px sans-serif">${(p.name || "?")[0]}</div><span style="background:#0D3B2B;color:#fff;font:700 11px sans-serif;padding:2px 7px;border-radius:8px;white-space:nowrap">${p.name}</span></div>`,
});

export const useMapData = () => {
  const [d, setD] = useState(null);
  const tick = useCallback(() => api.get("/admin/map").then((r) => setD(r.data)).catch(() => {}), []);
  useEffect(() => { tick(); const id = setInterval(tick, 30000); return () => clearInterval(id); }, [tick]);
  useSync(tick);
  return d;
};

export const itemsOf = (d) => !d ? [] : [
  ...d.customers.map((c) => ({ layer: "customers", key: `c-${c.user_id}`, lat: c.lat, lng: c.lng, title: c.name, sub: `${c.type || "Customer"} · ${c.bookings} bookings`, open: { type: "customer", id: c.user_id, title: c.name } })),
  ...d.bookings.map((b) => ({ layer: b.kind, key: `b-${b.booking_id}`, lat: b.lat, lng: b.lng, title: `${b.booking_id} · ${b.customer}`, sub: `${b.date} · ${b.slot_label}${b.staff_name ? ` · ${b.staff_name}` : ""}`, open: { type: "booking", data: b, title: b.booking_id } })),
  ...d.partners.filter((p) => p.location?.lat).map((p) => ({ layer: p.live ? "online" : "offline", key: `p-${p.staff_id}`, lat: p.location.lat, lng: p.location.lng, partner: p, title: p.name, sub: `${p.vehicle_type || "Partner"} · ${p.open_jobs} open`, open: { type: "partner", data: p, title: p.name } })),
];

const Fit = ({ pts, focus }) => {
  const map = useMap();
  const key = pts.length;
  useEffect(() => { if (pts.length) map.fitBounds(pts, { padding: [40, 40], maxZoom: 14 }); }, [key]); // eslint-disable-line
  useEffect(() => { if (focus) map.flyTo([focus.lat, focus.lng], 16, { duration: 0.6 }); }, [focus]); // eslint-disable-line
  return null;
};

export const MapCanvas = ({ items, on, focus, className = "h-full" }) => {
  const open = useDetail();
  const shown = items.filter((i) => on[i.layer]);
  return (
    <MapContainer center={JAIPUR} zoom={12} className={`relative z-0 w-full ${className}`} scrollWheelZoom>
      <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap" />
      <Fit pts={shown.map((i) => [i.lat, i.lng])} focus={focus} />
      {on.radius && items.filter((i) => i.partner && on[i.layer]).map((i) => {
        const sa = i.partner.service_area;
        return <Circle key={`r-${i.key}`} center={sa ? [sa.lat, sa.lng] : [i.lat, i.lng]} radius={(sa?.radius_km || 5) * 1000} pathOptions={{ color: COLOR.radius, weight: 1, fillOpacity: 0.06, dashArray: "4 6" }} />;
      })}
      {shown.map((i) => (i.partner
        ? <Marker key={i.key} position={[i.lat, i.lng]} icon={truck(i.partner)} eventHandlers={{ click: () => open(i.open) }} />
        : <CircleMarker key={i.key} center={[i.lat, i.lng]} radius={i.layer === "customers" ? 6 : 10} eventHandlers={{ click: () => open(i.open) }}
            pathOptions={{ color: "#fff", weight: 2, fillColor: COLOR[i.layer], fillOpacity: 0.95 }}><Tooltip>{i.title}<br />{i.sub}</Tooltip></CircleMarker>))}
    </MapContainer>
  );
};

export const LayerChips = ({ on, setOn, counts }) => (
  <div className="flex flex-wrap gap-2" data-testid="map-layer-toggles">
    {LAYERS.map(([k, l, c]) => (
      <button key={k} onClick={() => setOn({ ...on, [k]: !on[k] })} data-testid={`map-toggle-${k}`}
        className={`inline-flex h-9 items-center gap-2 rounded-full border px-3 text-xs font-bold transition-colors ${on[k] ? "border-[#0D3B2B] bg-[#0D3B2B] text-white" : "border-[#D6DBD2] bg-white text-[#6B7F74]"}`}>
        <span className="h-2.5 w-2.5 rounded-full" style={{ background: c }} />{l}{counts[k] != null && <span className="opacity-70">{counts[k]}</span>}
      </button>
    ))}
  </div>
);

export const AdminMap = () => {
  const d = useMapData();
  const open = useDetail();
  const [on, setOn] = useState(Object.fromEntries(LAYERS.map(([k]) => [k, true])));
  const [q, setQ] = useState("");
  const [focus, setFocus] = useState(null);
  const items = useMemo(() => itemsOf(d), [d]);
  const counts = useMemo(() => Object.fromEntries(LAYERS.map(([k]) => [k, k === "radius" ? null : items.filter((i) => i.layer === k).length])), [items]);
  const list = items.filter((i) => on[i.layer] && `${i.title} ${i.sub}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="space-y-4">
      <LayerChips on={on} setOn={setOn} counts={counts} />
      <div className="grid gap-4 lg:grid-cols-[1fr_340px]">
        <div className="h-[calc(100vh-220px)] min-h-[460px] overflow-hidden rounded-2xl border border-[#E3E6DD]" data-testid="admin-map-canvas"><MapCanvas items={items} on={on} focus={focus} /></div>
        <div className="flex max-h-[calc(100vh-220px)] min-h-[300px] flex-col rounded-2xl border border-[#E3E6DD] bg-white" data-testid="admin-map-list">
          <label className="flex items-center gap-2 border-b border-[#EEF0E9] px-4 py-3"><Search className="h-4 w-4 text-[#6B7F74]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Filter ${list.length} on map…`} className="w-full bg-transparent text-sm outline-none" data-testid="admin-map-filter" /></label>
          <div className="flex-1 space-y-1 overflow-y-auto p-2">
            {list.map((i) => (
              <button key={i.key} onClick={() => { setFocus({ lat: i.lat, lng: i.lng, k: Date.now() }); open(i.open); }} data-testid={`map-item-${i.key}`}
                className="flex w-full items-start gap-3 rounded-xl px-3 py-2 text-left transition-colors hover:bg-[#F7F5EF]">
                <span className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: COLOR[i.layer] }} />
                <span className="min-w-0"><b className="block truncate text-sm text-[#0D3B2B]">{i.title}</b><span className="block truncate text-xs text-[#6B7F74]">{i.sub}</span></span>
              </button>
            ))}
            {d && !list.length && <p className="p-6 text-center text-sm text-[#6B7F74]">Nothing to show for these filters.</p>}
            {!d && <p className="p-6 text-center text-sm text-[#6B7F74]">Loading map…</p>}
          </div>
        </div>
      </div>
    </div>
  );
};
